import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Api } from "../../api/Assinafy"
import { Alert, Button, Icon, Viewport } from "../../components"
import { Container } from "../../layout"
import Sign, { SignNavbar } from "../../layout/Sign"
import "./styles.scss"

const contactGuidance =
  "Para receber o documento assinado, abra o link de assinatura que você recebeu ou entre em contato com quem enviou o documento."
const whatsappExhaustedMessage =
  "O documento assinado já foi reenviado. Para uma nova cópia, entre em contato com quem enviou o documento."

const responseCertifiedCopy = (data) => data?.certified_copy || data || {}

const SignedDocumentExpired = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")?.trim() || ""
  const [available, setAvailable] = useState(Boolean(token))
  const [notificationMethod, setNotificationMethod] = useState()
  const [resendsLeft, setResendsLeft] = useState()
  const [contactRequired, setContactRequired] = useState(!token)
  const [resending, setResending] = useState(false)
  const [success, setSuccess] = useState("")
  const [error, setError] = useState("")
  const whatsappExhausted = notificationMethod === "Whatsapp" && resendsLeft === 0
  const canResend = Boolean(token) && available && !contactRequired && !whatsappExhausted

  const resendCertifiedDocument = async () => {
    setResending(true)
    setSuccess("")
    setError("")

    try {
      const response = await Api("signer/resendExpiredCertifiedCopy")(token)
      const payload = response?.data?.data || {}
      const certifiedCopy = responseCertifiedCopy(payload)
      const sentTo = payload.sent_to || certifiedCopy.sent_to

      if (Object.prototype.hasOwnProperty.call(certifiedCopy, "available")) {
        setAvailable(certifiedCopy.available)
      }
      setNotificationMethod(certifiedCopy.notification_method)
      setResendsLeft(certifiedCopy.resends_left)
      setSuccess(
        sentTo
          ? `Enviamos o documento assinado para ${sentTo}. O link vale por 30 minutos.`
          : "Enviamos o documento assinado pelo canal cadastrado. O link vale por 30 minutos.",
      )
    } catch (requestError) {
      const status = requestError?.response?.status
      const responseData = requestError?.response?.data || {}
      const certifiedCopy = responseCertifiedCopy(responseData.data || responseData)
      const errorNotificationMethod = certifiedCopy.notification_method || notificationMethod
      const errorResendsLeft = certifiedCopy.resends_left
      const errorAvailable = certifiedCopy.available

      if (status === 404 || status === 409) {
        setAvailable(false)
        setContactRequired(true)
      } else if (
        status === 429 &&
        (errorNotificationMethod === "Whatsapp" || errorResendsLeft === 0)
      ) {
        setAvailable(false)
        setNotificationMethod("Whatsapp")
        setResendsLeft(0)
      } else if (status === 429) {
        if (errorNotificationMethod !== "Email" || errorAvailable === false) {
          setAvailable(false)
        }
        setError(responseData.message || "Não foi possível reenviar o documento agora.")
      } else {
        setError(responseData.message || "Não foi possível enviar a mensagem")
      }
    } finally {
      setResending(false)
    }
  }

  return (
    <Sign className="sign-response signed-document-expired">
      <SignNavbar toggler={false} />
      <Viewport className="signed-document-expired-viewport">
        <Container>
          <section className="signed-document-expired-card" aria-labelledby="signed-document-expired-title">
            <div className="signed-document-expired-icon" aria-hidden="true">
              <Icon id="icon-warning" size="36" />
            </div>
            <p className="signed-document-expired-label">Documento assinado</p>
            <h1 id="signed-document-expired-title">Este link expirou.</h1>
            <p className="signed-document-expired-description">
              Por segurança, os links para baixar documentos assinados têm prazo de validade.
            </p>
            {token && !contactRequired ? (
              <p className="signed-document-expired-description">
                Solicite um novo link e nós o enviaremos para o mesmo e-mail ou WhatsApp usado na assinatura. O novo
                link será válido por 30 minutos.
              </p>
            ) : null}
            {contactRequired ? <p className="signed-document-expired-guidance">{contactGuidance}</p> : null}
            {success ? <Alert variant="success">{success}</Alert> : null}
            {error ? <Alert variant="warning">{error}</Alert> : null}
            {whatsappExhausted ? <Alert variant="warning">{whatsappExhaustedMessage}</Alert> : null}
            {canResend ? (
              <Button variant="primary" loading={resending} onClick={resendCertifiedDocument}>
                <Icon id="icon-email" className="me-1" size="18" />
                REENVIAR DOCUMENTO ASSINADO
              </Button>
            ) : null}
          </section>
        </Container>
      </Viewport>
    </Sign>
  )
}

export default SignedDocumentExpired
